<template>
  <div v-if="!showResponse" class="reserva-pases-container">
    <div>
      <h2>Reservar pase</h2>
      <p class="color-gray">
        Reservá tu pase seleccionando el tipo de actividad o nivel, sede a la que deseas concurrir, fecha y hora de la
        clase.
      </p>
    </div>
    <div class="reserva-pases">
      <Select
        label="Actividades contratadas"
        :options="pasesDisponibles"
        v-model="paseSelected"
        :disabled="isEditing() == true"
        @change="updateActividades"
      />
      <Select
        label="Tipo / Nivel"
        :options="actividadesDisponibles"
        v-model="actividadSelected"
        @change="updateSedes"
      />
      <Select label="Lugar" :options="sedesDisponibles" v-model="sedeSelected" @change="updateFecha" />
      <FechasMobile
        v-if="isMobile()"
        :fechas="fechasDisponibles"
        :fechaSelected="fechaSelected"
        @select="selectFecha"
      />
      <Fechas
        v-else
        label="Fecha"
        :fechas="fechasDisponibles"
        :fechaSelected="fechaSelected"
        v-model="fechaSelected"
        @change="selectFecha"
      />
      <HorarioMobile
        v-if="isMobile()"
        :horarios="horariosDisponibles"
        :horarioSelected="horarioSelected"
        @select="selectHorario"
      />
      <Horarios
        v-else
        label="Horario"
        :horarios="horariosDisponibles"
        horarioSelected=""
        v-model="horarioSelected"
        @change="selectHorario"
      />
    </div>
    <Button class="mt-50" type="button" text="Volver" classtype="back" @click="goToDashboard" />
    <Button
      class="mt-20"
      type="button"
      text="Reservar"
      classtype="primary"
      @click="reservarPase"
      :disabled="disableReserva"
    />
  </div>
  <Response
    v-else
    :success="responseSuccess"
    :title="responseTitle"
    :message="responseMessage"
    buttonText="Ver mis pases"
    :goToPage="DASHBOARD"
    showButton="yes"
  />
</template>
<script setup>
import { ref, computed, onBeforeMount } from "vue"
import {
  goToDashboard,
  getRouterPaseClaseId,
  OK,
  arrayOfDates,
  arrayOfStringDates,
  getDayLettersLong,
  DASHBOARD,
  horarioMenor,
} from "@/helpers"
import { usePasesStore, useClasesStore } from "@/store"

import Button from "../components/Button.vue"
import Select from "../components/Select.vue"
import Fechas from "../components/SelectorFecha.vue"
import Horarios from "../components/SelectorHorario.vue"

import FechasMobile from "../components/SelectorFechaMobile.vue"
import HorarioMobile from "../components/SelectorHorarioMobile.vue"
import Response from "../components/Response.vue"

const pasesStore = usePasesStore()
const clasesStore = useClasesStore()

const isMobile = () => {
  const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  return width < 720
}

const array_id_actividades = ref([])
const pasesFormateados = pasesStore.pasesFormateados

onBeforeMount(async () => {
  array_id_actividades.value = pasesStore.arrayIdActividadesPases
  clasesStore.getClases(array_id_actividades.value).then(() => {
    if (!isEditing()) {
      paseSelected.value = pasesDisponibles.value[0].value
      updateActividades()
    } else {
      paseSelected.value = paseId.value
      actividadSelected.value = pasesStore.paseSelected.idActividadClaseSelected
    }
  })
})

const paseClaseId = computed(() => {
  return getRouterPaseClaseId()
})

const paseId = computed(() => {
  return pasesStore.paseSelected.idPaseSelected
})

const paseActividadId = computed(() => {
  return pasesStore.paseSelected.idActividadClaseSelected
})

const isEditing = () => {
  return paseClaseId.value ? true : false
}

const pases = pasesFormateados.map((p) => {
  return {
    id: p.idactividad,
    label: p.actividad,
    value: p.idactividad,
  }
})

const paseSelected = ref(0)

const pasesDisponibles = computed(() => {
  if (isEditing() == true) {
    return pases.filter((pase) => pase.id == paseId.value)
  } else {
    return pases
  }
})

const getContratoPaseDetalleSelected = () => {
  const cpds = pasesFormateados.filter((p) => p.idactividad == paseSelected.value)
  if (cpds.length > 0) {
    return cpds[0].id
  } else {
    return null
  }
}

const getIdClase = () => {
  const pid = paseSelected.value
  if (clasesStore.clases[pid]) {
    const cl = clasesStore.clases[pid].filter(
      (c) =>
        c.idtipoactividad == actividadSelected.value &&
        c.idsede == sedeSelected.value &&
        c.dia == getDayLettersLong(fechaSelected.value) &&
        c.horario.substr(0, 5) == horarioSelected.value
    )
    if (cl.length > 0) {
      return cl[0].idclase
    } else {
      return null
    }
  } else {
    return null
  }
}

const updateActividades = () => {
  actividadSelected.value = actividadesDisponibles.value[0].value
}

const actividadesDisponibles = computed(() => {
  const pid = paseSelected.value
  return clasesStore.actividadesFormateadas[pid]
})

const actividadSelected = ref(0)

const sedesDisponibles = computed(() => {
  const pid = paseSelected.value
  if (clasesStore.clases[pid]) {
    const sedesActividad = clasesStore.clases[pid]
      .filter((clase) => clase.idtipoactividad == actividadSelected.value)
      .map((cl) => cl.idsede)
    return clasesStore.sedesFormateadas[pid].filter((sede) => sedesActividad.includes(sede.id))
  } else {
    return clasesStore.clases
  }
})

const sedeSelected = ref(1)

const updateSedes = () => {
  sedeSelected.value = sedesDisponibles.value[0].value
  updateFecha() // TODO: ojota acá
}

const fechasDisponibles = computed(() => {
  const pid = paseSelected.value
  if (clasesStore.clases[pid]) {
    const diasDisponibles = clasesStore.clases[pid]
      .filter((clase) => {
        return clase.idtipoactividad == actividadSelected.value && clase.idsede == sedeSelected.value
      })
      .map((c) => c.dia)
    return arrayOfStringDates()
      .filter((f) => {
        return diasDisponibles.includes(f.diaSemana)
      })
      .map((fc) => fc.fecha)
      .slice(0, 3)
  } else {
    return arrayOfStringDates()
      .map((fc) => fc.fecha)
      .slice(0, 3)
  }
})

const fechas = arrayOfStringDates().map((sd) => sd.fecha)

const fechaSelected = ref(fechas[0])

const updateFecha = () => {
  fechaSelected.value = fechasDisponibles.value[0]
  horarioSelected.value = null
}

const selectFecha = (newFecha) => {
  fechaSelected.value = newFecha
  horarioSelected.value = null
}

const horarios = ["09:00", "10:00", "11:00", "12:00", "17:00", "18:00"]

const claseCompleta = (clase) => {
  return parseInt(clase.totalInscriptos) >= parseInt(clase.cupo)
}

const horariosDisponibles = computed(() => {
  const pid = paseSelected.value
  if (clasesStore.clases[pid]) {
    return clasesStore.clases[pid]
      .filter((c) => {
        return (
          c.idtipoactividad == actividadSelected.value &&
          c.idsede == sedeSelected.value &&
          c.dia == getDayLettersLong(fechaSelected.value)
        )
      })
      .map((h) => {
        return {
          horario: h.horario.substr(0, 5),
          disabled:
            horarioMenor(fechaSelected.value, h.horario.substr(0, 5), pasesStore.inscription_limit) || claseCompleta(h),
        }
      })
      .sort((a, b) => a.horario > b.horario)
  } else {
    return horarios
  }
})

const horarioSelected = ref(null)

const selectHorario = (newHorario) => {
  if (isMobile()) {
    if (newHorario == horarioSelected.value) {
      horarioSelected.value = null
    } else {
      horarioSelected.value = newHorario
    }
  } else {
    horarioSelected.value = newHorario
  }
}

const disableReserva = computed(() => {
  return horarioSelected.value ? false : true
})

const showResponse = ref(false)
const responseSuccess = ref(false)
const responseTitle = ref("")
const responseMessage = ref("")

const reservarPase = () => {
  let texto = "reservado"
  if (isEditing() == true) {
    pasesStore.cancelPase(paseClaseId.value).then((response) => {
      if (response.result == OK) {
        texto = "modificado"
      } else {
        responseSuccess.value = false
        responseTitle.value = "¡Error, el pase no pudo ser modificado!"
        responseMessage.value = response.message
        showResponse.value = true
        return
      }
    })
  }
  const data = {
    // pase: paseSelected.value,
    fecha: fechaSelected.value,
    id_actividad: actividadSelected.value,
    id_clase: getIdClase(),
    id_contrato_pase_detalles: getContratoPaseDetalleSelected(),
    // sede: sedeSelected.value,
    // horario: horarioSelected.value,
  }
  clasesStore.reservarClase(data).then((response) => {
    if (response.result == OK) {
      responseSuccess.value = true
      responseTitle.value = `¡Pase ${texto} con éxito!`
      responseMessage.value =
        "Has reservado con éxito el pase a una actividad. Muchas gracias por utilizar nuestros servicios."
    } else {
      responseSuccess.value = false
      responseTitle.value = "Error al reservar el pase."
      responseMessage.value = response.message
    }
    showResponse.value = true
  })
}
</script>
<style>
.reserva-pases-container {
  max-width: 360px;
  padding: 0 28px;
}

.reserva-pases {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
</style>
